import { EditorConfig, ElementNode, LexicalNode, SerializedElementNode } from 'lexical';

// Define the serialized format for the CalloutNode.
export type SerializedCalloutNode = SerializedElementNode & {
  type: 'callout';
  version: 1;
};

export class CalloutNode extends ElementNode {
  // This will hold the DOM element where Lexical inserts child nodes.
  __contentDOM: HTMLElement | null = null;

  static getType(): string {
    return 'callout';
  }

  static clone(node: CalloutNode): CalloutNode {
    return new CalloutNode(node.__key);
  }

  constructor(key?: string) {
    super(key);
  }

  /**
   * Create a DOM structure for the callout node.
   *
   * The structure is:
   *
   * <div class="callout">
   *   <span class="callout-emoji" contenteditable="false">💡</span>
   *   <div class="callout-content"></div>
   * </div>
   *
   * The content container (".callout-content") is a direct child of the
   * returned container, so Lexical will insert nested nodes there.
   */
  createDOM(config: EditorConfig): HTMLElement {
    // Outer container for the callout.
    const container = document.createElement('div');
    container.className = 'callout';

    const emoji = document.createElement('span');
    emoji.className = 'callout-emoji';
    emoji.textContent = '💡';
    emoji.setAttribute('contenteditable', 'false');
    container.appendChild(emoji);

    return container;
  }

  updateDOM(prevNode: CalloutNode, dom: HTMLElement, config: EditorConfig): boolean {
    // Our structure is static; no updates required.
    return false;
  }

  exportJSON(): SerializedCalloutNode {
    return {
      ...super.exportJSON(),
      type: 'callout',
      version: 1,
    };
  }

  static importJSON(): CalloutNode {
    return new CalloutNode();
  }

  // Controls where text can be inserted.
  canInsertTextBefore(): boolean {
    return false;
  }
  canInsertTextAfter(): boolean {
    return false;
  }
  canBeEmpty(): boolean {
    return true;
  }
  isInline(): boolean {
    return false;
  }
}

/**
 * Helper function to create a new CalloutNode.
 */
export function $createCalloutNode(): CalloutNode {
  return new CalloutNode();
}

/**
 * Type guard to check if a node is a CalloutNode.
 */
export function $isCalloutNode(node: LexicalNode | null | undefined): node is CalloutNode {
  return node instanceof CalloutNode;
}
